// Global Instructions Rule Applied!
//
// Generic paginated list hook for the mobile browse screens. Calls the descriptor's
// service adapter with an explicit page + pageSize (true server pagination) and accumulates
// rows in LOCAL state — deliberately NOT in the module Redux slices: writing thin list rows
// there would shadow `useRecordDetail`'s full cold-RPC fetch, and the deals slice can't
// append anyway. Tapping a row just deep-links to the existing detail screen.

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { ModuleListDescriptor } from './listDescriptors.ts'
import { FilterValue } from '../../../../app_modules/global_configs/tableViewPreferences.ts'
import { getCachedList, setCachedList, clearCachedList, listCacheKey } from './listCache.ts'

export interface UseMobileListResult {
  rows: any[]
  loading: boolean
  loadingMore: boolean
  error: string | null
  hasMore: boolean
  totalCount: number
  loadMore: () => void
  reload: () => void
  /** Cache key for this module+filters view (used for scroll restoration). */
  cacheKey: string
}

export function useMobileList(
  descriptor: ModuleListDescriptor | null,
  extraFilters?: FilterValue[],
  selectionId?: string | null
): UseMobileListResult {
  const [rows, setRows] = useState<any[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(Boolean(descriptor))
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Bumped on every fetch start; a stale fetch (id mismatch) discards its result, so a
  // reload always supersedes an in-flight loadMore (e.g. when the stage filter changes).
  const requestIdRef = useRef(0)
  // Dedupes rapid loadMore calls (IntersectionObserver can fire several times per flick).
  const moreInFlightRef = useRef(false)

  // Serialize extraFilters + the top-selector value into a stable effect/callback key, so
  // switching the selection (prospects' Fund) re-keys the cache and reloads page 1.
  const extraKey = JSON.stringify({ filters: extraFilters ?? [], selectionId: selectionId ?? null })
  const cacheKey = listCacheKey(descriptor?.module ?? 'none', extraKey)

  const runFetch = useCallback(
    async (pageToLoad: number, mode: 'reload' | 'more') => {
      if (!descriptor) return
      const reqId = ++requestIdRef.current
      if (mode === 'reload') {
        setLoading(true)
        setLoadingMore(false)
        setError(null)
      } else {
        setLoadingMore(true)
      }
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await descriptor.fetchPage(pageToLoad, descriptor.pageSize, token, extraFilters, selectionId)
        if (reqId !== requestIdRef.current) return // superseded by a newer reload
        const incoming = Array.isArray(res.data) ? res.data : []
        if (res.error) {
          setError(res.error)
          if (mode === 'reload') {
            setRows([])
            setHasMore(false)
          }
        } else {
          setError(null)
          if (mode === 'reload') {
            setRows(incoming)
          } else {
            setRows((prev) => {
              const seen = new Set(prev.map((r) => descriptor.rowId(r)))
              const merged = prev.slice()
              for (const r of incoming) {
                const key = descriptor.rowId(r)
                if (!seen.has(key)) {
                  seen.add(key)
                  merged.push(r)
                }
              }
              return merged
            })
          }
          setHasMore(Boolean(res.hasMore))
          setTotalCount(typeof res.totalCount === 'number' ? res.totalCount : incoming.length)
          setPage(pageToLoad)
        }
      } catch (e) {
        if (reqId === requestIdRef.current) {
          setError(e instanceof Error ? e.message : 'Failed to load')
          if (mode === 'reload') {
            setRows([])
            setHasMore(false)
          }
        }
      } finally {
        if (reqId === requestIdRef.current) {
          if (mode === 'reload') setLoading(false)
          else setLoadingMore(false)
        }
      }
    },
    // extraFilters + pipelineId captured via extraKey; descriptor object is module-level stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [descriptor, extraKey]
  )

  const reload = useCallback(() => {
    clearCachedList(cacheKey) // explicit refresh always refetches
    void runFetch(1, 'reload')
  }, [runFetch, cacheKey])

  const loadMore = useCallback(() => {
    if (moreInFlightRef.current || !hasMore) return
    moreInFlightRef.current = true
    void runFetch(page + 1, 'more').finally(() => {
      moreInFlightRef.current = false
    })
  }, [runFetch, hasMore, page])

  // (Re)load page 1 on mount / when the module or filters change — but hydrate instantly
  // from the in-memory cache (no spinner, no fetch) when we've seen this view before, so
  // returning from a detail screen is immediate.
  useEffect(() => {
    if (!descriptor) {
      setRows([])
      setLoading(false)
      setHasMore(false)
      return
    }
    const cached = getCachedList(cacheKey)
    if (cached && cached.rows.length > 0) {
      requestIdRef.current++ // supersede any in-flight fetch
      setRows(cached.rows)
      setPage(cached.page)
      setHasMore(cached.hasMore)
      setTotalCount(cached.totalCount)
      setError(null)
      setLoading(false)
      setLoadingMore(false)
      return
    }
    setRows([])
    setHasMore(false)
    setPage(1)
    void runFetch(1, 'reload')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptor?.module, extraKey])

  // Persist successful list state so a remount can hydrate it. Skip while loading or in an
  // error state (don't cache a half/failed load); a genuine empty result is cacheable.
  useEffect(() => {
    if (!descriptor || loading || error) return
    setCachedList(cacheKey, {
      rows,
      page,
      hasMore,
      totalCount,
      scrollTop: getCachedList(cacheKey)?.scrollTop ?? 0
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, page, hasMore, totalCount, loading, error, cacheKey])

  return { rows, loading, loadingMore, error, hasMore, totalCount, loadMore, reload, cacheKey }
}
